import type EddRepository from "@/core/compliance/repositories/EddRepository";
import type { AddEddDocumentInput } from "@/core/compliance/repositories/EddRepository";
import {
  EddStatus,
  EddTriggerReason,
  EddDocumentType,
  type EddRiskAssessment,
  type EddCaseSummary,
  type EddCaseDetail,
  type EddDocument,
  type EddHistoryEntry,
  type EddMfaChallenge,
} from "@/core/compliance/entities/EddCase";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";

function iso(offsetH: number): string {
  return new Date(Date.now() + offsetH * 60 * 60 * 1000).toISOString();
}

const SEED: EddCaseDetail[] = [
  { caseId: "edd-open", businessKey: "CUST-PEP-001", status: EddStatus.OPEN, triggerReason: EddTriggerReason.PEP, assignedAnalyst: null, createdAt: iso(-10), slaDeadline: iso(38), firstName: "Awa", lastName: "Traoré", submittedAt: null, l1ApprovedBy: null, l2ApprovedBy: null, decisionNotes: null },
  { caseId: "edd-prog", businessKey: "CUST-PEP-002", status: EddStatus.IN_PROGRESS, triggerReason: EddTriggerReason.PEP_WITH_SANCTIONED_COUNTRY, assignedAnalyst: "analyst.agence", createdAt: iso(-30), slaDeadline: iso(18), firstName: "Bakary", lastName: "Sissoko", submittedAt: null, l1ApprovedBy: null, l2ApprovedBy: null, decisionNotes: null },
  { caseId: "edd-l1", businessKey: "CUST-PEP-003", status: EddStatus.PENDING_L1, triggerReason: EddTriggerReason.HIGH_RISK, assignedAnalyst: "analyst.agence", createdAt: iso(-40), slaDeadline: iso(8), firstName: "Cheick", lastName: "Diarra", submittedAt: iso(-2), l1ApprovedBy: null, l2ApprovedBy: null, decisionNotes: null },
  { caseId: "edd-l2", businessKey: "CUST-PEP-004", status: EddStatus.PENDING_L2, triggerReason: EddTriggerReason.PEP, assignedAnalyst: "analyst.agence", createdAt: iso(-50), slaDeadline: iso(4), firstName: "Daouda", lastName: "Coulibaly", submittedAt: iso(-6), l1ApprovedBy: "agent.l1", l2ApprovedBy: null, decisionNotes: null },
  { caseId: "edd-susp", businessKey: "CUST-PEP-005", status: EddStatus.SUSPENDED, triggerReason: EddTriggerReason.MANUAL, assignedAnalyst: "analyst.agence", createdAt: iso(-60), slaDeadline: null, firstName: "Eve", lastName: "Bamba", submittedAt: null, l1ApprovedBy: null, l2ApprovedBy: null, decisionNotes: null },
];

export default class FakeEddRepositoryBackend implements EddRepository {
  private cases: EddCaseDetail[] = SEED.map((c) => ({ ...c }));
  private readonly docs: Map<string, EddDocument[]> = new Map([
    [
      "edd-prog",
      [
        { documentId: "doc-1", type: EddDocumentType.SOURCE_OF_FUNDS, fileName: "releve.pdf", comment: "Relevé bancaire", uploadedBy: "analyst.agence", uploadedAt: iso(-20), mimeType: "application/pdf", sizeBytes: 102400 },
      ],
    ],
  ]);

  private async delay<T>(value: T, ms = 400): Promise<T> {
    await new Promise((r) => setTimeout(r, ms));
    return value;
  }

  private find(caseId: string): EddCaseDetail | undefined {
    return this.cases.find((c) => c.caseId === caseId);
  }

  private setStatus(caseId: string, status: EddStatus): void {
    this.cases = this.cases.map((c) => (c.caseId === caseId ? { ...c, status } : c));
  }

  private summary(c: EddCaseDetail): EddCaseSummary {
    return {
      caseId: c.caseId, businessKey: c.businessKey, status: c.status, triggerReason: c.triggerReason,
      assignedAnalyst: c.assignedAnalyst, createdAt: c.createdAt, slaDeadline: c.slaDeadline,
      firstName: c.firstName, lastName: c.lastName,
    };
  }

  async getCasesByStatus(status: EddStatus): Promise<RepositoryResult<EddCaseSummary[]>> {
    const list = this.cases.filter((c) => c.status === status).map((c) => this.summary(c));
    return RepositoryResultFactory.success(await this.delay(list));
  }
  async getAssignedCases(analyst: string): Promise<RepositoryResult<EddCaseSummary[]>> {
    const list = this.cases.filter((c) => c.assignedAnalyst === analyst).map((c) => this.summary(c));
    return RepositoryResultFactory.success(await this.delay(list));
  }
  async getCase(caseId: string): Promise<RepositoryResult<EddCaseDetail>> {
    const found = this.find(caseId);
    if (!found) return RepositoryResultFactory.success(await this.delay(this.cases[0]));
    return RepositoryResultFactory.success(await this.delay({ ...found }));
  }
  async getDocuments(caseId: string): Promise<RepositoryResult<EddDocument[]>> {
    const existing = this.docs.get(caseId);
    return RepositoryResultFactory.success(await this.delay(existing ? [...existing] : []));
  }
  async getHistory(caseId: string): Promise<RepositoryResult<EddHistoryEntry[]>> {
    const c = this.find(caseId);
    const history: EddHistoryEntry[] = [
      { timestamp: c?.createdAt ?? iso(-10), actor: "system", action: "OPENED", fromStatus: null, toStatus: EddStatus.OPEN, note: "Auto-ouvert (PEP)" },
    ];
    return RepositoryResultFactory.success(await this.delay(history));
  }
  async addDocument(caseId: string, input: AddEddDocumentInput): Promise<RepositoryResult<void>> {
    const existing = this.docs.get(caseId);
    const list = existing ? [...existing] : [];
    list.push({ documentId: `doc-${list.length + 1}`, type: input.type, fileName: input.file.name, comment: input.comment ?? null, uploadedBy: "current.user", uploadedAt: iso(0), mimeType: input.file.type || null, sizeBytes: input.file.size });
    this.docs.set(caseId, list);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async submitL1(caseId: string): Promise<RepositoryResult<void>> {
    this.setStatus(caseId, EddStatus.PENDING_L1);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async assign(caseId: string, analyst: string): Promise<RepositoryResult<void>> {
    this.cases = this.cases.map((c) =>
      c.caseId === caseId ? { ...c, status: EddStatus.IN_PROGRESS, assignedAnalyst: analyst } : c,
    );
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async suspend(caseId: string, _reason: string): Promise<RepositoryResult<void>> {
    this.setStatus(caseId, EddStatus.SUSPENDED);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async resume(caseId: string): Promise<RepositoryResult<void>> {
    this.setStatus(caseId, EddStatus.IN_PROGRESS);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async requestMfaChallenge(_caseId: string): Promise<RepositoryResult<EddMfaChallenge>> {
    return RepositoryResultFactory.success(
      await this.delay({ sentTo: "d***r@bni.ci", ttlMinutes: 5 }),
    );
  }
  async approveL1(caseId: string, _comment?: string, _otpCode?: string): Promise<RepositoryResult<void>> {
    this.setStatus(caseId, EddStatus.PENDING_L2);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async approveL2(
    caseId: string,
    _riskAssessment: EddRiskAssessment,
    _conclusion?: string,
    _otpCode?: string,
  ): Promise<RepositoryResult<void>> {
    this.setStatus(caseId, EddStatus.APPROVED);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
  async reject(caseId: string, _reason: string, _otpCode?: string): Promise<RepositoryResult<void>> {
    this.setStatus(caseId, EddStatus.REJECTED);
    return RepositoryResultFactory.success(await this.delay(undefined));
  }
}
